import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

type BadgeVariant =
  | 'default'
  | 'secondary'
  | 'destructive'
  | 'outline'
  | 'success'
  | 'warning';

/**
 * Shared status pills.
 *
 * These are the ONE place that maps a status string to a color. The Products,
 * Orders and detail pages all render status through these components, so a
 * color or label change happens here once instead of in every page. Pass
 * `className` (e.g. `text-[10px]`) to size the pill for dense list rows.
 */

function StatusPill({
  label,
  variant,
  className,
}: {
  label: string;
  variant: BadgeVariant;
  className?: string;
}) {
  return (
    <Badge variant={variant} className={cn('capitalize', className)}>
      {label}
    </Badge>
  );
}

/** Product lifecycle status — active / draft / archived. */
export function ProductStatusBadge({
  status,
  className,
}: {
  status: string;
  className?: string;
}) {
  const variant: BadgeVariant =
    status === 'active' ? 'success' : status === 'draft' ? 'warning' : 'secondary';
  return <StatusPill label={status} variant={variant} className={className} />;
}

/** Order payment status — paid / pending / refunded / partially refunded / ... */
export function FinancialStatusBadge({
  status,
  className,
}: {
  status: string | null;
  className?: string;
}) {
  if (!status) return <span className="text-xs text-muted-foreground">{'—'}</span>;
  const variant: BadgeVariant =
    status === 'paid'
      ? 'success'
      : status === 'refunded'
        ? 'destructive'
        : status === 'pending'
          ? 'warning'
          : 'secondary';
  return <StatusPill label={status} variant={variant} className={className} />;
}

/** Order fulfillment status — fulfilled / partial / unfulfilled (null). */
export function FulfillmentStatusBadge({
  status,
  className,
}: {
  status: string | null;
  className?: string;
}) {
  if (!status) {
    return (
      <Badge variant="outline" className={cn('capitalize', className)}>
        Unfulfilled
      </Badge>
    );
  }
  const variant: BadgeVariant = status === 'fulfilled' ? 'success' : 'warning';
  return <StatusPill label={status} variant={variant} className={className} />;
}
